import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Select, Spin, Tabs, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import apiClient from "../api/apiClient";
import { getProfileDomains, getSelectedCompanyProfiles } from "../utils/activityProfiles";

const labels = { commercial: "Gestion commerciale", logistics: "Logistique" };

export default function WarehousePaymentRoutes({ form, warehouse, companies, user, visible, onReadyChange }) {
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const companyId = warehouse?.company_id;
  const scopes = useMemo(() => {
    const company = companies.find((item) => Number(item.id) === Number(companyId));
    const domains = getProfileDomains(company || getSelectedCompanyProfiles(user, companyId));
    const profileScopes = domains.map((domain) => domain === "logistique" ? "logistics" : domain)
      .filter((scope) => Object.keys(labels).includes(scope));
    if (domains.length) return profileScopes;
    const permissions = user?.permissions || [];
    const granted = Object.keys(labels).filter((scope) => permissions.some((key) =>
      key.startsWith(scope === "commercial" ? "Gestion Commerciale." : "logistics.")));
    // Older companies can lack an activity profile; administration still offers
    // explicit, separate configurations and never combines account lists.
    return granted.length && !user?.is_superadmin ? granted : Object.keys(labels);
  }, [companies, companyId, user]);
  const scopeKey = scopes.join(",");

  useEffect(() => {
    onReadyChange(false);
    form.setFieldsValue({ payment_routes: undefined });
    setOptions({}); setError("");
    if (!visible || !warehouse?.id || !companyId || !scopeKey) { setLoading(false); return undefined; }
    const controller = new AbortController();
    setLoading(true);
    Promise.all(scopeKey.split(",").map(async (scope) => {
      const { data } = await apiClient.get(`/api/warehouses/${warehouse.id}/payment-routes`, {
        signal: controller.signal, params: { company_id: companyId, module_scope: scope, include_options: true },
      });
      if (!data.success || !Array.isArray(data.data)) throw new Error("Réponse de routage invalide.");
      return [scope, data];
    })).then((results) => {
      if (controller.signal.aborted) return;
      setOptions(Object.fromEntries(results.map(([scope, data]) => [scope, data.options || {}])));
      form.setFieldsValue({ payment_routes: Object.fromEntries(results.map(([scope, data]) =>
        [scope, data.data.map(({ payment_mode_id, treasury_account_id }) => ({ payment_mode_id: Number(payment_mode_id), treasury_account_id: Number(treasury_account_id) }))])) });
      onReadyChange(true);
    }).catch((failure) => {
      if (!controller.signal.aborted) setError(failure.response?.data?.error || "Impossible de charger le routage des paiements.");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [visible, warehouse?.id, companyId, scopeKey, form, onReadyChange, retry]);

  if (!warehouse) return <Typography.Text type="secondary">Enregistrez l'établissement pour configurer ses paiements.</Typography.Text>;
  if (!scopes.length) return <Typography.Text type="secondary">Aucun module de paiement concerné pour cet établissement.</Typography.Text>;
  if (loading) return <Spin size="small" />;
  if (error) return <Alert type="error" showIcon message={error} action={<Button onClick={() => setRetry((value) => value + 1)}>Réessayer</Button>} />;

  const content = (scope) => {
    const accounts = (options[scope]?.treasury_accounts || []).filter((account) => Number(account.is_active) === 1);
    const modes = options[scope]?.payment_modes || [];
    return <Card size="small" className="warehouse-form-modal__pos-card">
      <Typography.Paragraph type="secondary" className="warehouse-payment-routes__hint">
        {scope === "logistics" ? "Le compte associé au mode choisi est proposé pour les paiements clients, fournisseurs et Charges Admin. Vous pouvez choisir un autre compte compatible."
          : "Le POS résout automatiquement le compte associé au mode choisi. Ce compte est également proposé dans les autres formulaires commerciaux."}
      </Typography.Paragraph>
      <Form.List name={["payment_routes", scope]}>
        {(fields, { add, remove }) => <>
          {!fields.length && <Typography.Paragraph type="secondary" className="warehouse-payment-routes__hint">
            {scope === "logistics" ? "Aucun routage configuré. Le compte restera à choisir lors du paiement."
              : "Aucun routage configuré. Le POS utilisera le compte par défaut du type, si disponible."}
          </Typography.Paragraph>}
          {fields.map((field) => <Row gutter={16} key={field.key}>
            <Col xs={24} md={10}><Form.Item name={[field.name, "payment_mode_id"]} label="Mode de paiement" rules={[{ required: true, message: "Sélectionnez un mode" }]}>
              <Select placeholder="Mode de paiement" style={{ height: 44 }} showSearch optionFilterProp="label" options={modes.map((mode) => ({ value: Number(mode.id), label: mode.name }))} />
            </Form.Item></Col>
            <Col xs={24} md={10}><Form.Item name={[field.name, "treasury_account_id"]} label="Compte de trésorerie" rules={[{ required: true, message: "Sélectionnez un compte" }]}>
              <Select placeholder="Compte de trésorerie" style={{ height: 44 }} showSearch optionFilterProp="label" options={accounts.map((account) => ({ value: Number(account.id), label: `${account.name}${Number(account.is_default) === 1 ? " — par défaut" : ""}` }))} />
            </Form.Item></Col>
            <Col xs={24} md={4}><Form.Item label=" "><Button danger className="warehouse-payment-routes__remove" style={{ minHeight: 44 }} icon={<DeleteOutlined aria-hidden />} onClick={() => remove(field.name)} block>Supprimer</Button></Form.Item></Col>
          </Row>)}
          <Button type="dashed" style={{ minHeight: 44 }} icon={<PlusOutlined aria-hidden />} onClick={() => add()} disabled={!modes.length || !accounts.length}>Ajouter un routage</Button>
          {!accounts.length && <Alert type="warning" showIcon style={{ marginTop: 12 }} message={`Aucun compte de trésorerie ${scope === "logistics" ? "logistique" : "commercial"} actif n'est disponible pour cet établissement.`} />}
          {!modes.length && <Alert type="warning" showIcon style={{ marginTop: 12 }} message="Aucun mode de paiement disponible pour cet établissement." />}
        </>}
      </Form.List>
    </Card>;
  };
  return scopes.length === 1 ? <><Typography.Paragraph strong>{labels[scopes[0]]}</Typography.Paragraph>{content(scopes[0])}</>
    : <Tabs items={scopes.map((scope) => ({ key: scope, label: labels[scope], forceRender: true, children: content(scope) }))} />;
}
